import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import homeimg from '../assets/images/homeimg.jpg'

const QuotesSlider = () => {
  const quotes = [
    "Your period is not an inconvenience. It's a vital sign of your health.",
    "Menstruation is not a problem to be solved, but a natural process to be understood.",
    "Strong women lift each other up. Let's end the stigma around periods.",
    "Tracking your cycle is the first step to understanding your body's unique rhythm.",
    "Periods are normal. The shame around them is what needs to change.",
    "Say no to abortion — every heartbeat matters.",
    "Your period is a god-given gift, a sign of life and strength.",
    "Respect your body, honor its natural cycle.",
    "Empower girls with knowledge, not shame.",
    "Every life is precious. Protect, respect, and support motherhood.",
    "Break the silence, break the stigma. Talk about periods.",
    "Strong is the woman who understands her cycle and owns it.",
    "Save lives by supporting menstrual health and hygiene."
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [charIndex, setCharIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    let typingInterval;

    if (isTyping) {
      typingInterval = setInterval(() => {
        setDisplayedText((prev) => prev + quotes[currentIndex][charIndex]);
        setCharIndex((prev) => prev + 1);
      }, 30); // Faster typing speed
    }

    if (charIndex === quotes[currentIndex]?.length) {
      clearInterval(typingInterval);
      setIsTyping(false);
      setTimeout(() => {
        setIsTyping(true);
        setCharIndex(0);
        setDisplayedText("");
        setCurrentIndex((prev) => (prev === quotes.length - 1 ? 0 : prev + 1));
      }, 2000); // Shorter pause
    }

    return () => clearInterval(typingInterval);
  }, [charIndex, isTyping, currentIndex, quotes]);

  return (
    <section className="w-full pb-10 px-4 md:px-8 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-pink-200 opacity-20 blur-3xl -z-10"></div>
      <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-purple-200 opacity-20 blur-3xl -z-10"></div>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Left: Image with animation */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="w-full lg:w-[500px]"
          >
            <div className="relative group">
              <img
                src={homeimg}
                alt="Health & Wellness"
                className="rounded-3xl shadow-xl w-full h-auto object-cover transform transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-600/20 to-pink-500/10 rounded-3xl"></div>
            </div>
          </motion.div>

          {/* Right: Quotes */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2"
          >
            <h2 className="text-3xl font-nunito md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600 mb-6 text-center lg:text-center">
              Words of Wisdom
            </h2>
            
            <div className="relative">
              <div className="bg-white/90 backdrop-blur-sm p-6 md:p-8 rounded-3xl shadow-lg border border-white/20 relative overflow-hidden">
                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-pink-500/10 rounded-bl-full -mr-6 -mt-6"></div>
                
                <div className="text-4xl font-dancing font-medium text-purple-900 min-h-[150px] flex items-center justify-center lg:justify-start">
                  <span>{displayedText}</span>
                  {isTyping && (
                    <span className="animate-pulse text-purple-600 ml-1">|</span>
                  )}
                </div>
                
                {/* Quote indicator dots */}
                <div className="flex justify-center lg:justify-start mt-6 space-x-2">
                  {quotes.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentIndex(index);
                        setCharIndex(0);
                        setDisplayedText("");
                        setIsTyping(true);
                      }}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentIndex 
                          ? "bg-pink-500 w-4" 
                          : "bg-pink-200 hover:bg-pink-300"
                      }`}
                      aria-label={`Go to quote ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QuotesSlider;